import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const ResetPassword = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError(t('Invalid or missing reset token.'));
      setTokenValid(false);
      return;
    }

    // Verify token validity
    const verifyToken = async () => {
      try {
        await axios.get(`/api/auth/verify-reset-token?token=${encodeURIComponent(token)}`);
        setTokenValid(true);
      } catch (error) {
        console.error('Token verification failed:', error);
        setError(t('Invalid or expired reset token.'));
        setTokenValid(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (formData.password !== formData.confirmPassword) {
      setError(t('Passwords do not match.'));
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError(t('Password must be at least 6 characters long.'));
      setLoading(false);
      return;
    }

    try {
      await axios.post('/api/auth/reset-password', {
        token,
        newPassword: formData.password
      });
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error('Password reset failed:', error);
      setError(error.response?.data?.message || t('Failed to reset password. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (tokenValid === false) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1 className="login-title">{t('Invalid Reset Link')}</h1>
          
          <div className="alert alert-error">
            {error}
          </div>

          <div className="text-center mt-20">
            <Link 
              to="/forgot-password" 
              className="btn btn-primary"
              style={{ marginRight: '10px' }}
            >
              {t('Request New Reset Link')}
            </Link>
            <Link 
              to="/login" 
              className="btn btn-secondary"
            >
              {t('Back to Login')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h1 className="login-title">{t('Password Reset Successful!')}</h1>
            
            <div className="alert alert-success">
              {t('Your password has been successfully reset. You will be redirected to the login page in a few seconds.')}
            </div>

            <div className="text-center mt-20">
              <Link 
                to="/login" 
                className="btn btn-primary"
              >
                {t('Go to Login')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (tokenValid === null) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="loading">{t('Verifying reset link...')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">{t('Set New Password')}</h1>
        
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              {t('New Password')}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className="form-input"
              value={formData.password}
              onChange={handleInputChange}
              required
              disabled={loading}
              minLength="6"
              placeholder={t('Enter new password (min 6 characters)')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              {t('Confirm New Password')}
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              className="form-input"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              disabled={loading}
              minLength="6"
              placeholder={t('Confirm new password')}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={loading || !formData.password || !formData.confirmPassword}
          >
            {loading ? t('Resetting...') : t('Reset Password')}
          </button>
        </form>

        <div className="text-center mt-20">
          <Link 
            to="/login" 
            style={{ 
              color: 'var(--primary-blue)', 
              textDecoration: 'none',
              fontSize: '14px'
            }}
          >
            ← {t('Back to Login')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
